import React, { useEffect, useState } from 'react';
import { Pencil, RotateCcw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { formatApiError } from '@/utils/apiError';

const FIELDS = [
  ['eyebrow', 'Small top label'],
  ['headline', 'Headline'],
  ['intro', 'Intro line'],
  ['submit_label', 'Button text'],
  ['success_title', 'Thank-you title'],
  ['success_body', 'Thank-you message'],
  ['footer_note', 'Small footer note'],
];
const COURSE_FIELDS = [
  ['courses_label', '"Which courses" label'],
  ['message_label', 'Message label'],
  ['message_placeholder', 'Message placeholder'],
  ['hear_about_label', '"Hear about us" label'],
];
const KEYS = ['eyebrow', 'headline', 'intro', 'submit_label', 'success_title', 'success_body', 'footer_note', 'message_label', 'message_placeholder', 'courses_label', 'hear_about_label', 'show_message', 'show_courses', 'show_hear_about', 'hear_about_required'];

export function OxFormEditor({ formKey, label, path }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const isTraining = formKey === 'training';

  useEffect(() => {
    fetch(`${API_URL}/api/staff/embed-form-config/${formKey}`, { credentials: 'include' })
      .then((r) => r.json()).then(setCfg).catch(() => {});
  }, [formKey]);

  if (!cfg) return null;
  const update = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/embed-form-config/${formKey}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(KEYS.map((k) => [k, cfg[k]]))),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Save failed');
      setCfg((c) => ({ ...c, ...data }));
      setDirty(false);
      toast.success(`${label} form updated — live immediately.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this form\u2019s wording back to the Ox Resin defaults?')) return;
    const res = await fetch(`${API_URL}/api/staff/embed-form-config/${formKey}`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json();
    setCfg((c) => ({ ...c, ...data }));
    setDirty(false);
    toast.success('Reset to defaults.');
  };

  return (
    <Card data-testid={`ox-form-editor-${formKey}`}>
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1 basis-[200px]">
          <div className="text-sm font-bold text-gray-900">{label}</div>
          <div className="text-xs text-gray-500 truncate">{cfg.headline}{cfg.updated_at ? ' · customised' : ' · default wording'}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild><a href={path} target="_blank" rel="noreferrer" data-testid={`ox-form-preview-${formKey}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview</a></Button>
          <Button size="sm" variant={open ? 'default' : 'outline'} onClick={() => setOpen((o) => !o)} data-testid={`ox-form-edit-${formKey}`}>
            <Pencil className="w-3.5 h-3.5 mr-1" /> {open ? 'Close' : 'Edit wording'}
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === 'intro' || k === 'success_body' ? 'md:col-span-2' : ''}>
                <Label className="text-xs font-semibold text-gray-600">{l}</Label>
                <Input value={cfg[k] || ''} onChange={(e) => update({ [k]: e.target.value })} className="mt-1" data-testid={`ox-form-${formKey}-${k}`} />
              </div>
            ))}
            {COURSE_FIELDS.filter(([k]) => !(isTraining && k.startsWith('message'))).map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs font-semibold text-gray-600">{l}</Label>
                <Input value={cfg[k] || ''} onChange={(e) => update({ [k]: e.target.value })} className="mt-1" data-testid={`ox-form-${formKey}-${k}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 pt-1">
            <Toggle label='Show "Which courses" tick boxes' checked={cfg.show_courses} onChange={(v) => update({ show_courses: v })} testid={`ox-form-${formKey}-show_courses`} />
            {!isTraining && <Toggle label="Show message box" checked={cfg.show_message} onChange={(v) => update({ show_message: v })} testid={`ox-form-${formKey}-show_message`} />}
            <Toggle label='Show "Where did you hear about us?"' checked={cfg.show_hear_about} onChange={(v) => update({ show_hear_about: v })} testid={`ox-form-${formKey}-show_hear_about`} />
            {cfg.show_hear_about && <Toggle label='Make "hear about us" required' checked={cfg.hear_about_required} onChange={(v) => update({ hear_about_required: v })} testid={`ox-form-${formKey}-hear_about_required`} />}
          </div>
          <div className="flex flex-wrap gap-2 justify-end pt-1">
            <Button size="sm" variant="ghost" onClick={reset} className="text-gray-500" data-testid={`ox-form-reset-${formKey}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to defaults</Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-[#006400] hover:bg-[#004d00] text-white" data-testid={`ox-form-save-${formKey}`}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
      <span className="text-sm text-gray-700">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}
